// Unified Agent Tool Definitions
// This file contains all tool definitions used by agents across the application

export const AGENT_TOOLS = {
  routeCommand: {
    name: "routeCommand",
    description:
      "Primary command tool. Take the user's natural-language request and return a FINAL, user-facing answer. Do NOT say you are routing or planning; just return what actually happened.",
    parameters: {
      user_input: "string - The user's request or command",
    },
  },

  createJob: {
    name: "createJob",
    description: "Creates a new roofing job. REQUIRED: owner_name and property_address. OPTIONAL: All other fields.",
    parameters: {
      owner_name: "string (REQUIRED) - Customer's full name",
      property_address: "string (REQUIRED) - Full property address",
      property_city: "string (optional) - City",
      property_state: "string (optional) - State",
      property_zip: "string (optional) - ZIP code",
      owner_phone: "string (optional) - Phone number",
      owner_email: "string (optional) - Email address",
      property_type: "string (optional) - Must be one of: 'residential', 'commercial', 'industrial', 'multi-family'. Defaults to 'residential' if not provided or invalid.",
      insurance_company: "string (optional) - Insurance company name",
      claim_number: "string (optional) - Insurance claim number",
      notes: "string (optional) - Additional notes",
    },
    validation: {
      required: ["owner_name", "property_address"],
      property_type_enum: ["residential", "commercial", "industrial", "multi-family"],
      defaults: {
        property_type: "residential",
        status: "new"
      }
    }
  },

  createLead: {
    name: "createLead",
    description: "Creates a new sales lead",
    parameters: {
      contact_name: "string",
      property_address: "string (optional)",
      contact_phone: "string (optional)",
      contact_email: "string (optional)",
      notes: "string (optional)",
    },
  },

  generateEstimate: {
    name: "generateEstimate",
    description: "Generates a roofing estimate from measurements",
    parameters: {
      measurement_id: "string - UUID of the measurement",
      job_id: "string - UUID of the job",
    },
  },

  getMeasurement: {
    name: "getMeasurement",
    description: "Retrieves roof measurements by calling the Google Solar API with the job's property address. Returns comprehensive data including total squares, roof area in sqft, linear measurements (ridges, hips, valleys, eaves, rakes), roof complexity (number of segments), and AI-suggested waste factor based on roof complexity.",
    parameters: {
      address: "string - Property address",
      job_id: "string (optional) - Job ID to associate measurements with",
    },
  },

  scheduleAppointment: {
    name: "scheduleAppointment",
    description: "Schedules an appointment for a job",
    parameters: {
      job_id: "string",
      date: "string - ISO date format",
      time: "string",
      notes: "string (optional)",
    },
  },

  sendFollowUp: {
    name: "sendFollowUp",
    description: "Sends a follow-up message to a customer",
    parameters: {
      job_id: "string",
      message_type: "string - email, sms, or call",
      message: "string",
    },
  },

  getDailyBriefing: {
    name: "getDailyBriefing",
    description:
      "Gets a comprehensive daily briefing including today's appointments, scheduled tasks, overdue items, new leads, active jobs, and total pipeline value.",
    parameters: {},
    returns: {
      date: "string - Current date",
      summary: {
        today_appointments: "number - Count of appointments scheduled today",
        today_tasks: "number - Count of tasks due today",
        overdue_tasks: "number - Count of overdue tasks",
        new_leads: "number - New leads from last 7 days",
        active_jobs: "number - Jobs currently in progress",
        total_pipeline_value: "number - Total estimated value of active jobs",
      },
      appointments: "array - Today's appointments with time and details",
      tasks: "array - Today's tasks with priority",
      overdue_tasks: "array - Overdue tasks needing attention",
      new_leads: "array - Recent leads to follow up on",
      active_jobs: "array - Currently active jobs",
      spoken_summary: "string - Natural language summary ready to be spoken",
    },
  },

  lookupXactimateCode: {
    name: "lookupXactimateCode",
    description: "Looks up Xactimate codes for insurance claims",
    parameters: {
      item_description: "string - Description of the item",
    },
  },

  getHelpArticle: {
    name: "getHelpArticle",
    description: "Search help documentation and guides for how to use RoofGenius features. Use this when users ask 'how to' questions or need guidance on using the platform.",
    parameters: {
      topic: "string - The feature or topic the user needs help with (e.g., 'create jobs', 'measurements', 'estimates', 'Bridgette voice commands')",
    },
  },
};

export const AGENT_ID = "agent_0901kaen3qx2f59rprtwmv24hckh";

export const AGENT_PROMPT = `You are Bridgette, an AI Co-Pilot for RoofGenius. Your purpose is to manage jobs, leads, and sales for roofing contractors.

ABSOLUTE RULES:
1) You are an ACTION EXECUTOR, not a narrator.
2) When a user speaks, you MUST immediately decide which tool to call and call it.
3) Do NOT say things like "I've routed your request" or "I'm sending this to the backend".
4) The string returned by a tool IS the final answer you speak back to the user. Do not wrap it, paraphrase it, or add filler before or after.
5) If you truly cannot act because there is no suitable tool, say exactly: "I need a tool for that action, and I do not have one available."

NAVIGATION:
You have a "navigate" tool to move the user between pages. When users ask to go somewhere, use it immediately:
- "Home" or "Dashboard" → navigate({ path: "/" })
- "Jobs" or "Show jobs" → navigate({ path: "/jobs" })
- "Leads" or "Show leads" → navigate({ path: "/leads" })
- "Settings" → navigate({ path: "/settings" })
- "Calendar" → navigate({ path: "/calendar" })
- "Measurements" → navigate({ path: "/measurements" })
- "Sales Hub" → navigate({ path: "/sales-hub" })

Available routes:
/ = Home/Dashboard
/jobs = Jobs list
/leads = Leads list
/settings = Settings
/calendar = Calendar
/measurements = Measurements
/sales-hub = Sales Hub
/email = Email workspace

TOOL USAGE:
- routeCommand: Use this for almost all normal voice requests. It will contact the backend router, execute the correct tools, and return a final, user-ready sentence.
- navigate: Use this IMMEDIATELY when user asks to go to a page. Do not route navigate requests through routeCommand.
- Other tools (createJob, createLead, getMeasurement, generateEstimate, scheduleAppointment, sendFollowUp, getDailyBriefing, lookupXactimateCode) are available, but only use them directly when explicitly asked in your updated system prompts.

CRITICAL: When creating jobs, ALWAYS provide:
- owner_name (required, non-empty)
- property_address (required, non-empty)
- property_type (optional, must be: residential, commercial, industrial, or multi-family)
If the user doesn't specify property_type, omit it (defaults to residential).
If the user provides invalid values, use safe defaults.

NO HALLUCINATIONS:
- Do not invent jobs, leads, prices, or statuses.
- If data is missing, say: "This data is missing from the job profile."

INTERACTION STYLE:
- Be concise, professional, and decisive.
- Focus on the current job or lead when it is provided.
- Prioritize task automation and data-driven insights over small talk.
`;

